import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { queryClient } from "./lib/queryClient";
import { AppErrorBoundary } from "./components/feedback/ErrorBoundary";
import { ToastProvider } from "./components/feedback/Toast";
import { ConfirmProvider } from "./components/feedback/ConfirmDialog";
import { AuthProvider } from "./auth/AuthContext";
import { installHttpInterceptors } from "./lib/httpClient";
import { App } from "./App";
import { applyBranding } from "./branding/applyBranding";
import { loadPersistedBranding } from "./branding/brandingStore";
import "./styles/global.css";

// Attach CSRF + auto-refresh interceptors before any request is made.
installHttpInterceptors();

// Write the Layer 1 brand variables (and the derived on-brand text color and
// focus ring) onto :root before the first paint, so no frame renders with the
// build-time fallback palette.
applyBranding();

// Then pull the identity the deployment actually SAVED. This runs at boot
// rather than from the authenticated shell because the sign-in screen is a
// branded surface too, and it renders before any session exists. The request
// is unauthenticated (GET /settings/branding is public) and its failure is
// silent: branding is presentation, never a reason to block sign-in.
void loadPersistedBranding().catch(() => {});

const container = document.getElementById("root");
if (!container) {
  throw new Error('Root element "#root" not found');
}

createRoot(container).render(
  <StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <ConfirmProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </StrictMode>,
);
